/**
 * ComplementaryBillsReport.jsx — R6
 * Route: /pos/reports/complementary-bills
 * API:   GET /api/pos/reports/complementary-bills
 */
import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Popover, Spin, Tag } from "antd";
import { Download, Filter, Calendar } from "react-feather";
import DateRangePicker from "react-bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import SelectComponent from "react-select";
import PrintReportPreview from "../../reports/PrintReportPreview";

const getToken    = () => localStorage.getItem("token");
const getApiUrl = () => {
  const base = (process.env.REACT_APP_API_URL || "http://localhost:3000/api").replace(/\/+$/, "");
  return base ;//.endsWith("/pos") ? base : `${base}/pos`;
};
const getCurrency = () => localStorage.getItem("currencycode") || "₹";
const BUSINESS_DATE_KEYS = ["posbusiness_date", "business_date", "pos_business_date"];
const getBizDate = () => {
  for (const key of BUSINESS_DATE_KEYS) {
    const d = localStorage.getItem(key);
    if (d && moment(d).isValid()) return moment(d).startOf("day");
  }
  return getBizDate().clone().startOf("day");
};
const pillStyle   = (bg,color)=>({background:bg,color,fontWeight:800,fontSize:12,padding:"5px 10px",borderRadius:999,border:`1px solid ${color}33`,whiteSpace:"nowrap"});
const downloadCSV = (columns,data,filename)=>{
  const h=columns.filter(c=>c.dataIndex).map(c=>c.title).join(",");
  const r=data.map(row=>columns.filter(c=>c.dataIndex).map(c=>`"${String(row[c.dataIndex]??"").replace(/"/g,'""')}"`).join(","));
  const blob=new Blob([[h,...r].join("\n")],{type:"text/csv;charset=utf-8;"}); const url=URL.createObjectURL(blob);
  const a=document.createElement("a"); a.href=url; a.download=filename; a.click(); URL.revokeObjectURL(url);
};

const ComplementaryBillsReport = () => {
  const currency=getCurrency(); const apiURL=getApiUrl();
  const [dateRange,     setDateRange]     = useState({startDate:getBizDate(),endDate:getBizDate()});
  const [loading,       setLoading]       = useState(false);
  const [bills,         setBills]         = useState([]);
  const [totals,        setTotals]        = useState({});
  const [filterVisible, setFilterVisible] = useState(false);
  const [previewVisible,setPreviewVisible]= useState(false);
  const [exportData,    setExportData]    = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [filters,       setFilters]       = useState({ order_type:null, min_amount:"" });

  const orderTypeOptions=["Table Service","Room Service","Delivery","Counter"].map(v=>({value:v,label:v}));

  const fetchReport=useCallback(async()=>{
    setLoading(true);
    try{
      const params={
        start_date:dateRange.startDate.format("YYYY-MM-DD"),
        end_date:dateRange.endDate.format("YYYY-MM-DD"),
        ...(filters.order_type&&{order_type:filters.order_type.value}),
        ...(filters.min_amount&&Number(filters.min_amount)>0&&{min_amount:filters.min_amount}),
      };
      const res=await axios.get(`${apiURL}/pos/complementary-bills`,{headers:{Authorization:`Bearer ${getToken()}`},params});
      if(res.data.success){setBills(res.data.bills||[]);setTotals(res.data.totals||{});}
    }catch(err){Swal.fire({icon:"error",title:"Error",text:err?.response?.data?.error||err.message});}
    finally{setLoading(false);}
  },[dateRange,filters,apiURL]);

  useEffect(()=>{fetchReport();},[fetchReport]);

  const columns=[
    {title:"Bill No",                  dataIndex:"display_order_no",  width:130,fixed:"left"},
    {title:"Date",                     dataIndex:"business_date",     width:120},
    {title:"Time",                     dataIndex:"order_time",        width:80},
    {title:"Order Type",               dataIndex:"order_type",        width:130},
    {title:"Table/Room",               dataIndex:"table_room",        width:110},
    {title:"Items",                    dataIndex:"item_count",        width:70,align:"right"},
    {title:`Subtotal (${currency})`,   dataIndex:"subtotal",          width:120,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Tax (${currency})`,        dataIndex:"tax_amount",        width:100,align:"right",render:v=>Number(v||0).toFixed(2)},
    {title:`Waived (${currency})`,     dataIndex:"waived_amount",     width:120,align:"right",fixed:"right",
     render:v=><strong style={{color:"#dc2626"}}>{Number(v||0).toFixed(2)}</strong>},
    {title:"Raised By (User ID)",      dataIndex:"created_by",        width:150},
    {title:"Status",                   dataIndex:"status",            width:100,render:v=><Tag color={v==="Completed"?"green":"orange"}>{v}</Tag>},
    {title:"Settled",                  dataIndex:"is_settled",        width:75,align:"center",render:v=><Tag color={v?"green":"orange"}>{v?"Yes":"No"}</Tag>},
  ];

  const summaryRow=()=>(
    <Table.Summary.Row style={{background:"#fef2f2",fontWeight:700}}>
      <Table.Summary.Cell index={0} colSpan={5}>{totals.total_bills||0} Bills Total</Table.Summary.Cell>
      <Table.Summary.Cell/>
      <Table.Summary.Cell align="right">{currency} {Number(totals.total_subtotal_waived||0).toFixed(2)}</Table.Summary.Cell>
      <Table.Summary.Cell align="right">{currency} {Number(totals.total_tax_waived||0).toFixed(2)}</Table.Summary.Cell>
      <Table.Summary.Cell align="right">{currency} {Number(totals.total_amount_waived||0).toFixed(2)}</Table.Summary.Cell>
      <Table.Summary.Cell colSpan={3}/>
    </Table.Summary.Row>
  );

  const filterContent=(
    <div style={{width:240,display:"grid",gap:12}}>
      <div>
        <label style={{fontSize:12,fontWeight:700,display:"block",marginBottom:4}}>Order Type</label>
        <SelectComponent isClearable options={orderTypeOptions} value={filters.order_type}
          onChange={v=>setFilters(p=>({...p,order_type:v}))} styles={{control:b=>({...b,fontSize:13})}}/>
      </div>
      <div>
        <label style={{fontSize:12,fontWeight:700,display:"block",marginBottom:4}}>Min Waived Amount ({currency})</label>
        <input className="form-control" type="number" min="0" value={filters.min_amount}
          onChange={e=>setFilters(p=>({...p,min_amount:e.target.value}))}
          placeholder="e.g. 500 — flag high-value comps" style={{fontSize:13,height:36}}/>
      </div>
      <Button type="primary" size="small" block onClick={()=>{setFilterVisible(false);fetchReport();}}>Apply Filters</Button>
    </div>
  );

  return(
    <div className="page-wrapper"><div className="content container-fluid">
      <div className="row align-items-center mb-3">
        <div className="col-auto d-flex align-items-center gap-3">
          <h2 style={{fontSize:20,fontWeight:700,borderBottom:"2px solid #007bff",paddingBottom:4,margin:0}}>Complementary Bills Report</h2>
          <DateRangePicker
            key={`${dateRange.startDate.format("YYYY-MM-DD")}-${dateRange.endDate.format("YYYY-MM-DD")}`}
            initialSettings={{startDate:dateRange.startDate.toDate(),endDate:dateRange.endDate.toDate(),
              ranges:{Today:[getBizDate().clone().startOf("day").toDate(),getBizDate().clone().endOf("day").toDate()],
                "This Month":[getBizDate().clone().startOf("month").toDate(),getBizDate().clone().endOf("month").toDate()],
                "Last Month":[getBizDate().clone().subtract(1,"month").startOf("month").toDate(),getBizDate().clone().subtract(1,"month").endOf("month").toDate()]},
              locale:{format:"DD MMM YYYY",applyLabel:"Apply",cancelLabel:"Cancel"},alwaysShowCalendars:true,autoApply:false}}
            onApply={(_,p)=>setDateRange({startDate:p.startDate,endDate:p.endDate})}>
            <div className="input-group" style={{width:250}}>
              <input type="text" className="form-control text-center fw-bold" style={{height:38,fontSize:14,borderRight:"none",background:"#fff"}}
                value={`${dateRange.startDate.format("DD MMM YYYY")} – ${dateRange.endDate.format("DD MMM YYYY")}`} readOnly/>
              <span className="input-group-text" style={{background:"#fff",borderLeft:"none",height:38,cursor:"pointer"}}><Calendar size={15} color="#495057"/></span>
            </div>
          </DateRangePicker>
        </div>
        <div className="col-auto ms-auto d-flex align-items-center gap-2">
          {totals.total_bills&&(
            <>
              <span style={pillStyle("#fef2f2","#dc2626")}>{currency} {Number(totals.total_amount_waived||0).toFixed(0)} Waived</span>
              <span style={pillStyle("#eff6ff","#1d4ed8")}>{totals.total_bills} Bills</span>
            </>
          )}
          <Popover content={filterContent} title="Filters" trigger="click"
            open={filterVisible} onOpenChange={setFilterVisible} placement="bottomRight">
            <Button type="default" className="d-flex align-items-center" style={{height:38}}>
              <Filter size={15} className="me-2"/>Filter
            </Button>
          </Popover>
          <div className="dropdown">
            <Button type="primary" className="d-flex align-items-center dropdown-toggle" data-bs-toggle="dropdown" style={{height:38}}>
              <Download size={15} className="me-2"/>Download
            </Button>
            <ul className="dropdown-menu dropdown-menu-end">
              <li><button className="dropdown-item" onClick={()=>downloadCSV(columns,bills,`Comp_Bills_${dateRange.startDate.format("YYYYMMDD")}.csv`)}>Download CSV</button></li>
              <li><button className="dropdown-item" onClick={()=>{setExportData(bills);setExportHeaders(columns.filter(c=>c.dataIndex).map(c=>({label:c.title,key:c.dataIndex})));setPreviewVisible(true);}}>Print / PDF Preview</button></li>
            </ul>
          </div>
        </div>
      </div>
      {loading?<div className="text-center my-5"><Spin size="large"/></div>:(
        <Table columns={columns} dataSource={bills} rowKey={(r,i)=>r.order_id||i}
          scroll={{x:"max-content"}} size="small" bordered
          pagination={{pageSize:50,showTotal:t=>`${t} bills`}}
          summary={bills.length>0?summaryRow:undefined}/>
      )}
      <PrintReportPreview visible={previewVisible} onClose={()=>setPreviewVisible(false)}
        reportTitle={`Complementary Bills — ${dateRange.startDate.format("DD MMM YYYY")} to ${dateRange.endDate.format("DD MMM YYYY")}`}
        propertyName="" data={exportData} headers={exportHeaders} footerData="" totalColumns={[]} apiEndpoint="transactions/generate-pdf" apiUrl={apiURL}/>
    </div></div>
  );
};

export default ComplementaryBillsReport;
